import config from 'data/config';
import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { navigationSelector } from './navigationSlice';

const appSlice = createSlice({
  name: 'app',
  initialState: {
    isLoadingContent: false,
    isShowingUserSheet: false, // used for both user and partner sheets
    goToResetPassword: false,
    goToUserDetails: false,
    usersName: '',
    companyName: '',
    employeeLogonUrl: undefined,
    waitForLoadingToFinish: false,
    shugoLogonUrl: `${config.wfhUiUrl}/Login`,
    useShugoLogonUrl: false,
    partnerLogonUrl: `${config.masterDataReportsURL}/login.aspx`,
    switchRouteUrl: '',
    usePartnerLogonUrl: false,
    logonUrlIsSet: false,
    goToConfigureNotifications: false,
  },
  reducers: {
    logonUrlHasBeenSet(state, action) {
      state.logonUrlIsSet = action.payload;
    },
    setIsLoadingContent(state, action) {
      state.isLoadingContent = action.payload.loading;
      if (!action.payload.loading) {
        state.waitForLoadingToFinish = false;
      }
    },

    setIsShowingUserSheet(state, action) {
      state.isShowingUserSheet = action.payload.showing;
      state.usersName = action.payload.name;
    },

    setEmployeeLogonUrl(state, action) {
      state.employeeLogonUrl = action.payload.employeeLogonUrl;
    },

    setUserShugoLogonUrl(state, action) {
      // ensure it's always a true boolean
      state.useShugoLogonUrl = !!action.payload.useShugoLogonUrl;
    },

    setCompanyName(state, action) {
      state.companyName = action.payload.companyName;
    },

    setWaitForLoadingToFinish(state, action) {
      state.waitForLoadingToFinish = action.payload.waitForLoadingToFinish;
    },

    setGoToResetPassword(state, action) {
      state.goToResetPassword = action.payload.goToResetPassword;
    },

    setGoToUserDetails(state, action) {
      state.goToUserDetails = action.payload.goToUserDetails;
    },

    setSwitchRouteToUrl(state, action) {
      state.switchRouteUrl = action.payload.switchRouteUrl;
    },

    setUsePartnerLogonUrl(state, action) {
      state.usePartnerLogonUrl = action.payload.usePartnerLogonUrl;
    },

    setGoToConfigureNotifications(state, action) {
      state.goToConfigureNotifications =
        action.payload.goToConfigureNotifications;
    },
  },
});

// Public
export const {
  logonUrlHasBeenSet,
  setIsLoadingContent,
  setIsShowingUserSheet,
  setCompanyName,
  setEmployeeLogonUrl,
  setWaitForLoadingToFinish,
  setGoToResetPassword,
  setGoToUserDetails,
  setSwitchRouteToUrl,
  setUserShugoLogonUrl,
  setUsePartnerLogonUrl,
  setGoToConfigureNotifications,
} = appSlice.actions;

export const waitForLoadingToFinish = (callback, delay = 200) => {
  return async (dispatch, getState) => {
    dispatch(setWaitForLoadingToFinish({ waitForLoadingToFinish: true }));
    const intervalId = setInterval(() => {
      // This function waits for the iframe to load before calling the callback.
      // It uses waitForLoadingToFinish state, which is updated when isLoadingContent becomes false.
      // This prevents the callback from firing prematurely.
      const loading = getState().app.waitForLoadingToFinish;
      if (!loading) {
        clearInterval(intervalId);
        callback();
      }
    }, delay);
  };
};

export const appSelector = (state) => state.app;
export const selectIsShowingUserSheet = createSelector(
  appSelector,
  (app) => app.isShowingUserSheet
);

export const selectUsersName = createSelector(
  appSelector,
  (app) => app.usersName
);

export const selectCompanyName = createSelector(
  appSelector,
  (app) => app.companyName
);

export const selectGoToResetPassword = createSelector(
  appSelector,
  (app) => app.goToResetPassword
);

export const selectGoToUserDetails = createSelector(
  appSelector,
  (app) => app.goToUserDetails
);

export const selectGoToConfigureNotifications = createSelector(
  appSelector,
  (app) => app.goToConfigureNotifications
);

export const selectLogonUrls = createSelector(appSelector, (app) => ({
  employeeLogonUrl: app.employeeLogonUrl,
  shugoLogonUrl: app.shugoLogonUrl,
  useShugoLogonUrl: app.useShugoLogonUrl,
  partnerLogonUrl: app.partnerLogonUrl,
  usePartnerLogonUrl: app.usePartnerLogonUrl,
}));

export const selectNavigationAndApp = createSelector(
  [navigationSelector, appSelector],
  (navigation, app) => ({
    hasCompanyDrawer: navigation.hasCompanyDrawer,
    switchRouteUrl: app.switchRouteUrl,
  })
);

export default appSlice.reducer;
